import * as React from 'react';
import strings from 'strings/strings.json';
import useNeveForm from 'components/forms/NeveForm';
import useUploadRequest from 'hooks/useUploadRequest';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { FlexColSm, Headline3 } from 'components/Elements';
import { ErrorMessage } from 'components/forms/FormComponents';
import useModifyRequest from 'hooks/useModifyRequest';
import { logError } from 'lib/debug-helpers';
import { APIError } from 'lib/_api-helpers';
import { Activity, ActivityType } from 'models/Activity';
import { FileDocument, FileDocumentType } from 'models/FileDocument';
import { createGlobalErrorFromAPIError, createFieldErrorFromAPIError } from 'shared/error-messages';
import GeneratedContentSection from './shared/GeneratedContentSection';
import { Course } from 'models/Course';
import { Resource } from 'models/Resources';
import { VideoCard } from '../../FileCard';
import { VideoSelectionComponent } from '../../FileSelectionComponent';
import { LearningResourcesFormModal } from '../../LearningResourcesFormModal';
import ResourcesList from '../../ResourcesList';
import DeleteActivityAlert from './shared/DeleteActivityAlert';
import { EditTaskFormProps, FileCardComponentData, ModuleOnlyHasOneTask } from './shared/SharedEditTaskComponents';
import {
    ActivityFormState,
    ActivityDetailsInput,
    ResourcesInput,
    EditVideoTaskInput,
} from '../shared/SharedActivityFormComponents';
import { EditActivityFormCard } from '../shared/EditActivityFormCard';
import BaseDetailsForm from '../shared/DetailsForm';
import { UpdateFileFields, UpdateVideoTaskFields } from 'models/inputModels/CreateTaskRequest';
import { useFieldArray } from 'react-hook-form';

export default function EditVideoTaskForm({
    task: taskProp,
    returnToCourseStructure,
}: EditTaskFormProps): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { setCourseState, editMode, refetchCourse, setEditActivityId } = React.useContext(BuildCourseContext);

    const [task, setTask] = React.useState<Activity>(taskProp);
    const [globalErrorMessage, setGlobalErrorMessage] = React.useState<string>('');
    const [videoRequired, setVideoRequired] = React.useState<boolean>(false);
    const [resourcesModalOpen, setResourcesModalOpen] = React.useState(false);
    const [state, setState] = React.useState<ActivityFormState>('clean');
    const [deleteTaskAlertOpen, setDeleteAlertOpen] = React.useState(false);

    const [localVideoToBeUploaded, setLocalVideoToBeUploaded] = React.useState<File | undefined | null>(null);
    const [providerVideoToBeUploaded, setProviderVideoToBeUploaded] = React.useState<FileDocument>();
    const [videoCardData, setVideoCardComponentData] = React.useState<FileCardComponentData | null>(
        taskProp.videoDocument
            ? {
                  fileName: taskProp.videoDocument.filename,
                  fileDate: taskProp.videoDocument.dateUpdated,
                  filePath: taskProp.videoDocument.s3Filepath,
                  fileSource: 'provider',
              }
            : null,
    );

    const selectVideoButtonRef = React.useRef<HTMLButtonElement>(null);
    const addLinkButtonRef = React.useRef<HTMLButtonElement>(null);
    const removeVideoButtonRef = React.useRef<HTMLButtonElement>(null);

    const { upload, uploading: uploadingVideo } = useUploadRequest();
    const { modifyData: updateVideoTask, loading: updatingTask } = useModifyRequest<UpdateVideoTaskFields, boolean>(
        `activities/task/video/${task.id}`,
        'PUT',
    );

    React.useEffect(() => {
        return () => {
            setEditActivityId('');
        };
    }, []); //eslint-disable-line

    React.useEffect(() => {
        if (taskProp) {
            setTask(taskProp);
            setGlobalErrorMessage('');
            setVideoRequired(false);
            setLocalVideoToBeUploaded(undefined);
            setProviderVideoToBeUploaded(undefined);
        }
    }, [taskProp]);

    React.useEffect(() => {
        let data: FileCardComponentData | null;

        if (localVideoToBeUploaded) {
            data = {
                fileSource: 'local',
                fileName: localVideoToBeUploaded.name,
                fileDate: new Date(),
                filePath: URL.createObjectURL(localVideoToBeUploaded),
            };
        } else if (providerVideoToBeUploaded) {
            data = {
                fileSource: 'provider',
                fileName: providerVideoToBeUploaded.filename,
                fileDate: providerVideoToBeUploaded.dateUpdated,
                filePath: providerVideoToBeUploaded.s3Filepath,
            };
        } else if (task.videoDocument) {
            data = {
                fileSource: 'provider',
                fileName: task.videoDocument.filename,
                fileDate: task.videoDocument.dateUpdated,
                filePath: task.videoDocument.s3Filepath,
            };
        } else {
            data = null;
        }

        if (localVideoToBeUploaded || providerVideoToBeUploaded) {
            setState('dirty');
        }

        setVideoCardComponentData(data);
    }, [task, localVideoToBeUploaded, providerVideoToBeUploaded]);

    React.useEffect(() => {
        setVideoRequired(!videoCardData);
    }, [videoCardData]);

    const {
        register,
        handleSubmit,
        control,
        setError,
        watch,
        formState: { errors },
        reset,
    } = useNeveForm<EditVideoTaskInput>({
        title: task.title ?? '',
        description: task.description ?? '',
        durationInMins: task.durationInMins ?? 1,
        resources: task.resources.map((x) => ({ url: x.url, title: x.title })),
    });

    const {
        fields: resourceFields,
        append: appendResource,
        remove: removeResource,
    } = useFieldArray({
        control,
        name: 'resources',
    });

    const title = watch('title');
    const description = watch('description');
    const durationInMins = watch('durationInMins');
    const resources = watch('resources');

    React.useEffect(() => {
        const titleChanged = title !== taskProp.title;
        const descriptionChanged = description !== taskProp.description;
        const durationInMinsChanged = durationInMins != taskProp.durationInMins; // eslint-disable-line eqeqeq
        const videoChanged = videoCardData?.fileName !== taskProp.videoDocument?.filename;

        const resourcesChanged = !(
            resources.length === taskProp.resources.length &&
            resources.every((v, i) => v.title === taskProp.resources[i].title && v.url === taskProp.resources[i].url)
        );

        const changed =
            titleChanged || descriptionChanged || durationInMinsChanged || resourcesChanged || videoChanged;
        if (changed) {
            setState('dirty');
        }
    }, [title, description, durationInMins, resources, videoCardData]); // eslint-disable-line

    const addResource = (newResource: ResourcesInput) => {
        appendResource(newResource);
        addLinkButtonRef.current?.focus();
    };
    const deleteResource = (index: number) => {
        removeResource(index);
        addLinkButtonRef.current?.focus();
    };

    async function uploadVideo(): Promise<UpdateFileFields | null> {
        if (localVideoToBeUploaded) {
            const { errors, value } = await upload(localVideoToBeUploaded);

            if (errors && errors.length > 0) {
                logError(errors);
                updateErrors(errors);
                throw new Error('Video upload failed');
            }
            if (!value) {
                logError('Get presigned URL failed');
                updateErrors([{ field: 'global', code: 'get presigned URL failed' }]);
                throw new Error('Video upload failed');
            }
            return {
                type: 'new',
                filePath: value.s3Key,
                fileName: value.filename,
            };
        } else if (providerVideoToBeUploaded) {
            return {
                type: 'existing',
                id: providerVideoToBeUploaded.id,
                fileName: providerVideoToBeUploaded.filename,
                filePath: providerVideoToBeUploaded.s3Filepath,
            };
        } else if (task.videoDocument) {
            return {
                type: 'existing',
                id: task.videoDocument.id,
                fileName: task.videoDocument.filename,
                filePath: task.videoDocument.s3Filepath,
            };
        } else {
            return null;
        }
    }

    async function saveTask(formData: EditVideoTaskInput) {
        if (!videoCardData) {
            selectVideoButtonRef.current?.focus();
            return;
        }
        if (state !== 'dirty') {
            setState('saved');
            return;
        }

        let updateVideoFields: UpdateFileFields | null = null;
        try {
            updateVideoFields = await uploadVideo();
        } catch (error: any) {
            logError(error);
            return;
        }
        if (!updateVideoFields) return;

        if (!editMode) {
            const request: UpdateVideoTaskFields = {
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins ?? 1,
                resources: resources,
                video: updateVideoFields,
            };
            const { errors } = await updateVideoTask(request);
            if (errors) {
                logError(errors);
                updateErrors(errors);
                return;
            }
            reset(formData);
            await refetchCourse();
            setState('saved');
            return;
        }

        setCourseState((prev) => {
            const copy = structuredClone(prev) as Course;
            const module = copy.modules.find((m) => m.id === task.moduleId);
            if (!module) return prev;
            const taskToUpdate = module.activities.find((a) => a.id === task.id);
            if (!taskToUpdate) return prev;

            taskToUpdate.title = formData.title;
            taskToUpdate.description = formData.description;
            taskToUpdate.durationInMins = formData.durationInMins;
            taskToUpdate.resources = resources as Resource[];
            taskToUpdate.content = updateVideoFields.type === 'new' ? '' : taskToUpdate.content; //transcript to be re-generated
            taskToUpdate.videoDocument = {
                id: updateVideoFields.type === 'new' ? '' : updateVideoFields.id,
                filename: updateVideoFields.fileName,
                s3Filepath: updateVideoFields.filePath,
                documentType: FileDocumentType.VIDEO,
                dateUpdated: new Date(),
            };
            return copy;
        });

        reset(formData);
        setState('saved');
    }

    function updateErrors(apiErrors: APIError[]): void {
        apiErrors.forEach((apiError) => {
            const { field, code } = apiError;
            if (field === 'global') {
                return setGlobalErrorMessage(createGlobalErrorFromAPIError(code));
            }
            setError(field as keyof ActivityDetailsInput, createFieldErrorFromAPIError(field, code));
        });
    }

    const removeVideo = () => {
        setLocalVideoToBeUploaded(undefined);
        setProviderVideoToBeUploaded(undefined);
        setVideoCardComponentData(null);
        setTask({ ...task, content: '', videoDocument: null });
        selectVideoButtonRef.current?.focus();
    };

    const moduleOnlyHasOneTask = ModuleOnlyHasOneTask(course, task.moduleId);

    const DetailsAndLinks: JSX.Element = (
        <>
            <BaseDetailsForm register={register} errors={errors} autoFocus />
            <ResourcesList
                resources={resourceFields}
                toggleModalOpen={() => setResourcesModalOpen(!resourcesModalOpen)}
                deleteResource={deleteResource}
                disabled={uploadingVideo || updatingTask}
                addLinkButtonRef={addLinkButtonRef}
            />
        </>
    );

    const VideoAndTranscript: JSX.Element = (
        <FlexColSm>
            <Headline3>{strings.selectFileForm.addVideoTitle}</Headline3>
            {videoCardData ? (
                <VideoCard
                    fileName={videoCardData.fileName}
                    filePath={videoCardData.filePath}
                    source={videoCardData.fileSource}
                    disabled={uploadingVideo || updatingTask}
                    removeFile={removeVideo}
                    removeFileButtonRef={removeVideoButtonRef}
                />
            ) : (
                <VideoSelectionComponent
                    setFileFromLocal={(newFile) => {
                        setLocalVideoToBeUploaded(newFile);
                    }}
                    setFileFromProvider={setProviderVideoToBeUploaded}
                    setFileFromLocalMetaData={() => {}}
                    disableButton={uploadingVideo || updatingTask}
                    selectFileButtonRef={selectVideoButtonRef}
                />
            )}
            {videoRequired && <ErrorMessage>{strings.activityUpdatePage.videoRequired}</ErrorMessage>}
            {!editMode && videoCardData && !!task.videoDocument && <GeneratedContentSection task={task} />}
        </FlexColSm>
    );

    return (
        <>
            <EditActivityFormCard
                title={strings.activityDetailsForm.instructionEdit}
                onSubmit={handleSubmit(saveTask)}
                pageOne={DetailsAndLinks}
                pageTwo={VideoAndTranscript}
                error={globalErrorMessage}
                showLastActivityInModuleWarning={editMode && moduleOnlyHasOneTask}
                showSavedSuccessfully={state === 'saved' && !editMode}
                showSavedSuccessfullyEditMode={state === 'saved' && editMode}
                setDeleteAlertOpen={() => setDeleteAlertOpen((prev) => !prev)}
                disableDeleteTaskButton={uploadingVideo || updatingTask || (editMode && moduleOnlyHasOneTask)}
                disableSubmitButton={uploadingVideo || updatingTask || (editMode && state === 'clean')}
                submitting={uploadingVideo || updatingTask}
                activityType={ActivityType.TASK}
            />
            {resourcesModalOpen && (
                <LearningResourcesFormModal
                    closeModal={() => setResourcesModalOpen(false)}
                    addResource={addResource}
                />
            )}
            {deleteTaskAlertOpen && (
                <DeleteActivityAlert
                    taskId={task.id}
                    moduleId={module.id}
                    returnToCourseStructure={returnToCourseStructure}
                    setAlertOpen={setDeleteAlertOpen}
                />
            )}
        </>
    );
}
